import { defaultNodeConfigs } from "../pages/protected/workflows/CreateCampaign/flow/flowSettings";
import { useConfigStore, useModelsStore } from "../stores/ConfigStore";
import { useProfileStore, useSessionStore } from "../stores/UserStore";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useFlowStore } from "../stores/WorkflowStore";
import { getTimezoneOffset, incrementHour } from "./dates";

export async function executeSequentially(promises, onChange, onError) {
  let i = 0;

  for (const p of promises) {
    const result = await p().catch((err) => {
      onError?.(err);
    });
    onChange?.(result, i);

    i++;
  }
}

export function transformFilterToServer(filter, disableQueryTransform) {
  let query = filter?.quickFilterValues?.join(" ");

  function getItems(v, inGroup) {
    if (v?.values && v?.mode) {
      return {
        mode: v?.mode,
        values: v.values.map((_v) => getItems(_v, true)),
        id: v?.id,
      };
    }
    if (inGroup) {
      return {
        field_name: v?.field,
        type: v?.operator?.toLowerCase(),
        value: v?.value,
        id: v?.id,
      };
    }
    return {
      field_name: v?.field,
      type: v?.operator?.toLowerCase(),
      value: v?.value,
      id: v?.id,
    };
  }

  let result =
    query && !disableQueryTransform
      ? {
          mode: "and",
          values: [
            { field_name: "any_datafield", type: "contains", value: query },
          ],
        }
      : filter?.items?.length > 0
        ? {
            mode: filter?.logicOperator?.toLowerCase() || "or",
            values: filter?.items.map((item) => getItems(item)) || [],
          }
        : null;

  if (query && filter?.items?.length > 0) {
    result.values.push({
      mode: filter?.logicOperator?.toLowerCase(),
      values: filter?.items.map((item) => getItems(item)) || [],
    });
  }

  return result;
}

export function transformServerToFilter(serverFilter, query) {
  // Return default empty state if filter is null/undefined
  if (!serverFilter) {
    return { items: [], logicOperator: "or", quickFilterValues: [] };
  }

  let quickFilterValues = [];
  let items = [];
  // Default logic operator to the root mode, usually "and" or "or"
  let logicOperator = serverFilter?.mode || "or";

  // Helper function to map Server Nodes back to UI Items recursively
  function getUiItem(node) {
    // If the node has 'values' and 'mode', it is a nested group
    if (node?.values && node?.mode) {
      return {
        field: node.field_name,
        mode: node.mode,
        values: node.values.map(getUiItem),
      };
    }

    // Otherwise, it is a standard filter leaf
    return {
      id: node.id,
      field: node.field_name,
      operator: node.type,
      value: node.value,
    };
  }

  // DETECT PATTERN:
  // The original function wraps everything in an "AND" group if a global query exists.
  // We check if root is "AND" and contains the specific 'any_datafield' key.
  const isGlobalSearchWrapper =
    serverFilter?.mode === "and" &&
    serverFilter?.values?.some((v) => v.field_name === "any_datafield");

  if (isGlobalSearchWrapper) {
    // Pattern A: Global Search + (Optional) Item Group
    serverFilter.values.forEach((node) => {
      if (node.field_name === "any_datafield") {
        // Restore quickFilterValues
        // Original logic was .join(" "), so here we .split(" ")
        if (node.value) {
          quickFilterValues = node.value.split(" ");
        }
      } else if (node.values && node.mode) {
        // This group holds the original column filters
        // We restore the original Logic Operator from this inner group
        logicOperator = node.mode;
        items = node.values.map(getUiItem);
      } else {
        // Handle edge case where a single filter might sit alongside the query
        items.push(getUiItem(node));
      }
    });
  } else {
    // Pattern B: Standard Filter (No Global Search)
    // The root mode is the Logic Operator
    logicOperator = serverFilter?.mode || "or";
    items = (serverFilter?.values || []).map(getUiItem);
  }

  return {
    items,
    logicOperator,
    quickFilterValues: query ? query.split(" ") : quickFilterValues,
  };
}

export function transformSortToServer(sortModel) {
  const sort = sortModel?.[0];
  if (sort) {
    return {
      field_name: sort.field,
      sort_direction: sort.sort,
    };
  }
  return null;
}

export function filtersAreEqual(filterA, filterB) {
  if (!filterA || !filterB) return false;

  // Check mode first
  if (filterA.mode !== filterB.mode) return false;

  const aValues = filterA.values || [];
  const bValues = filterB.values || [];

  if (aValues.length !== bValues.length) return false;

  return aValues.every((aVal) =>
    bValues.some(
      (bVal) =>
        aVal.field_name === bVal.field_name &&
        aVal.type === bVal.type &&
        aVal.value === bVal.value,
    ),
  );
}

export function searchNormalize(query) {
  return query
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function filterDeaccentedSearch({ options, search }) {
  const splittedSearch = searchNormalize(search).split(" ");

  return options.filter((option) => {
    const words = searchNormalize(option.label).split(" ");

    return splittedSearch.every((searchWord) => {
      return words.some((word) => word.includes(searchWord));
    });
  });
}

export function getPermission(name) {
  const profile = useProfileStore.getState().profile;

  return profile?.permissions?.find?.((x) => x.name === name)?.value || false;
}

export function getLimit(permission, profile, type) {
  return profile?.permissions?.find((x) => x.name === permission)?.[type] || 0;
}

export function getObjectWithChangedProperties(obj1, obj2) {
  const changedProperties = {};

  for (const key in obj2) {
    if (Object.hasOwnProperty.call(obj2, key)) {
      if (
        !Object.hasOwnProperty.call(obj1, key) ||
        JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])
      ) {
        // Check if the property is not in obj1 or its value has changed
        changedProperties[key] = obj2[key];
      }
    }
  }

  return changedProperties;
}

export function sortDescId(rows) {
  if (rows?.length > 0) {
    return rows.sort((a, b) => b.id - a.id);
  }
  return [];
}

export function wrapDatafieldValue(v) {
  return `${v.startsWith("%") ? "{" : ""}${v.replaceAll("%", "")}${v.endsWith("%") ? "}" : ""}`;
  // return `<span class="var">${v.startsWith("%") ? "{" : ""}${v.replaceAll("%", "")}${v.endsWith("%") ? "}" : ""}</span> `;
}

export function getChartColors() {
  return [
    "blue",
    "secondary",
    "green",
    "cyan",
    "yellow",
    "orange",
    "teal",
    "violet",
    "pink",
    "grape",
  ];
}

export function sumDateStats(data) {
  const totals = {};

  data.forEach((stat) => {
    Object.keys(stat).forEach((key) => {
      // Skip non-numeric fields (e.g., "date")
      if (typeof stat[key] === "number") {
        totals[key] = (totals[key] || 0) + stat[key];
      }
    });
  });

  return totals;
}

export function getReportingRateColor(rate) {
  if (rate <= 4) {
    return "var(--mantine-color-red-5)";
  }
  if (rate <= 7) {
    return "var(--mantine-color-orange-5)";
  }
  if (rate <= 10) {
    return "var(--mantine-color-teal-5)";
  }
  return "var(--mantine-color-green-5)";
}
export function filterActionNodes(nodes) {
  return nodes.filter(
    (x) =>
      (x?.data?.actionType?.includes("email") ||
        x?.data?.actionType?.includes("smv") ||
        x?.data?.actionType?.includes("linkedin") ||
        x?.data?.actionType?.includes("sms")) &&
      x?.data?.actionType !== "linkedin_visit",
  );
}

export function getWorkflowBranches(workflow, forEvent) {
  const root = workflow.find(
    (step) => step.data && step.data.actionType === "dummy",
  );
  if (!root) return [];

  // Track visited nodes to avoid duplicate entries
  const visited = new Set();

  function collectBranch(startStep) {
    let branch = [];
    let queue = [startStep];

    while (queue.length > 0) {
      const current = queue.shift();

      if (visited.has(current.id)) continue; // Skip if already added to another branch

      visited.add(current.id);
      branch.push(current);

      let children = workflow.filter((step) => {
        if (!step.data || !step.data.source) return false;

        let sources = Array.isArray(step.data.source)
          ? step.data.source
          : [step.data.source];

        return sources.includes(current.id);
      });

      queue.push(...children);
    }

    if (forEvent) {
      return branch.filter((x) => x?.data?.actionType === "event");
    }
    return filterActionNodes(branch);
  }

  let branches = workflow
    .filter((step) => step.data && step.data.source === root.id)
    .map((branchStart) => collectBranch(branchStart));

  return branches;
}

export function getWorkflowBranchesNew(workflow, forEvent) {
  // 1. Trouver le point de départ (noeud racine)
  const root = workflow.find(
    (step) => step.data && step.data.actionType === "dummy",
  );
  if (!root) return [];

  const branches = [];

  // Fonction utilitaire pour récupérer les enfants d'un noeud donné
  const getChildren = (nodeId) => {
    return workflow.filter((step) => {
      if (!step.data || !step.data.source) return false;
      let sources = Array.isArray(step.data.source)
        ? step.data.source
        : [step.data.source];
      return sources.includes(nodeId);
    });
  };

  // 2. Parcours en profondeur (DFS) pour construire les branches de bout en bout
  const buildPaths = (node, currentPath) => {
    const children = getChildren(node.id);

    // Si le noeud n'a pas d'enfant, c'est une "feuille". La branche est terminée.
    if (children.length === 0) {
      branches.push(currentPath);
      return;
    }

    // Sinon, pour chaque embranchement, on duplique le chemin actuel et on continue
    for (const child of children) {
      buildPaths(child, [...currentPath, child]);
    }
  };

  // 3. On initialise la construction à partir des enfants directs du root
  const startNodes = getChildren(root.id);
  for (const startNode of startNodes) {
    buildPaths(startNode, [startNode]);
  }

  // 4. On applique ton filtrage final sur chaque branche complète trouvée
  return branches.map((branch) => {
    if (forEvent) {
      return branch.filter((x) => x?.data?.actionType === "event");
    }
    // Je suppose que filterActionNodes est importée ailleurs dans ton code
    return filterActionNodes(branch);
  });
}

export function getTagBackgroundColor(tag) {
  const tags = useConfigStore.getState().tags;
  let tagObj = tags.find((x) => x.name === tag);

  if (tagObj?.color) {
    return `#${tagObj.color}`;
  }
  // Get the first letter of the tag in lowercase
  const firstLetter = tag.charAt(0).toLowerCase();

  // Generate a color based on the character code of the first letter
  const charCode = firstLetter.charCodeAt(0);

  // Ensure it's a letter (a-z)
  if (charCode >= 97 && charCode <= 122) {
    // Generate a color using HSL (hue is based on the letter position in the alphabet)
    const hue = (charCode - 97) * 13; // 13 degrees between each letter
    return `hsl(${hue}, 70%, 90%)`; // HSL color
  }

  // Default color for non-alphabetic characters
  return "#E8EAED"; // Light gray
}

export function getTagsSwatches() {
  return [
    "#f3bdbd",
    "#bdd8f3",
    "#f3bdbd",
    "#f3bdd0",
    "#e8bdf3",
    "#cbbdf3",
    "#bdc8f3",
    "#bddaf3",
    "#bdedf3",
    "#bdf3e3",
    "#bdf3c7",
    "#ddf3bd",
    "#f3e3bd",
    "#f3d6bd",
  ];
}

export function downloadBlob(blob, filename) {
  // Create a temporary URL for the blob
  const url = URL.createObjectURL(blob);

  // Create a hidden anchor element
  const a = document.createElement("a");
  a.href = url;
  a.download = filename; // Set the file name for the download
  document.body.appendChild(a); // Append it to the DOM

  // Trigger the download
  a.click();

  // Clean up: remove the element and revoke the URL
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadFromUrl(url, filename) {
  // Create a hidden anchor element
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.download = filename; // Set the file name for the download
  document.body.appendChild(a); // Append it to the DOM

  // Trigger the download
  a.click();

  // Clean up: remove the element and revoke the URL
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function getCurrencySymbol(key) {
  switch (key) {
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "GBP":
      return "£";
    case "JPY":
      return "¥";
    case "CNY":
      return "¥";
    case "INR":
      return "₹";
    case "RUB":
      return "₽";
    case "KRW":
      return "₩";
    case "AUD":
      return "A$";
    case "CAD":
      return "C$";
    case "CHF":
      return "CHF";
    case "SEK":
      return "kr";
    case "NOK":
      return "kr";
    case "DKK":
      return "kr";
    case "PLN":
      return "zł";
    case "BRL":
      return "R$";
    case "MXN":
      return "$";
    case "ZAR":
      return "R";
    case "HKD":
      return "HK$";
    case "SGD":
      return "S$";
    case "NZD":
      return "NZ$";
    case "TRY":
      return "₺";
    case "THB":
      return "฿";
    case "IDR":
      return "Rp";
    case "MYR":
      return "RM";
    case "PHP":
      return "₱";
    case "ILS":
      return "₪";
    case "HUF":
      return "Ft";
    case "CZK":
      return "Kč";
    case "RON":
      return "lei";
    case "NGN":
      return "₦";
    case "PKR":
      return "₨";
    case "BDT":
      return "৳";
    default:
      return key;
  }
}

export function insertTextToInput(text, input) {
  let value = input.value;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  const before = value.substring(0, start);
  const after = value.substring(end);

  const newValue = before + text + after;
  input.value = newValue;

  // Reset the cursor after inserted text
  setTimeout(() => {
    const cursorPos = start + text.length;
    input.selectionStart = input.selectionEnd = cursorPos;
    input.focus();
  }, 0);
}

export function formatPhone(v) {
  try {
    const phoneNumber = parsePhoneNumberFromString(v);
    if (!phoneNumber) return v; // not valid

    // Example: format in international format
    return phoneNumber.formatInternational();

    // Or national format (depends on country)
    // return phoneNumber.formatNational()
  } catch (e) {
    return v;
  }
}

export function formatThousand(v) {
  return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function getEmbedUrl(youtubeUrl) {
  if (!youtubeUrl) return null;

  // If it's already an embed link, return it directly
  if (youtubeUrl.includes("youtube.com/embed/")) {
    return youtubeUrl;
  }

  // Regex to extract video ID
  const regExp = /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
  const match = youtubeUrl.match(regExp);

  const videoId = match && match[1].length === 11 ? match[1] : null;

  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

export function getYouTubeMaxThumbnail(youtubeUrl, name) {
  let thumbnail_url = name
    ? useSessionStore.getState().reseller?.videos?.find((x) => x.name === name)
        ?.thumbnail_url
    : null;

  if (thumbnail_url) {
    return thumbnail_url;
  }
  if (!youtubeUrl) return null;

  // Extract videoId
  const regExp = /^.*(?:youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/;
  const match = youtubeUrl.match(regExp);
  const videoId = match && match[1].length === 11 ? match[1] : null;

  return videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null;
}

export function setCookie(name, value) {
  // Convert the object to a JSON string
  var cookieValue = JSON.stringify(value);

  // Set the cookie with the JSON string
  var cookieString = name + "=" + encodeURIComponent(cookieValue) + "; path=/";
  document.cookie = cookieString;
}

export function parseStrToStates(str) {
  const regex =
    /^([+-])(\d+)\s+day\s+([+-])(\d+)\s+hour\s+([+-])(\d+)\s+minute$/;
  const match = str?.trim().match(regex);

  if (!match) {
    // fallback for "now" or invalid formats
    return { sign: "-", day: 0, hour: 0, minute: 0, date: new Date() };
  }

  const [_, signDay, day, signHour, hour, signMinute, minute] = match;

  // Ensure that signs are the same for consistency
  if (signDay !== signHour || signDay !== signMinute) {
    console.warn("Inconsistent signs in time string!");
  }

  const sign = signDay === "-" ? -1 : 1;
  const d = parseInt(day, 10);
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);

  // Start from "now"
  const baseDate = new Date();

  // Apply offsets
  const resultDate = new Date(baseDate);
  resultDate.setDate(resultDate.getDate() + sign * d);
  resultDate.setHours(resultDate.getHours() + sign * h);
  resultDate.setMinutes(resultDate.getMinutes() + sign * m);

  return {
    sign: signDay,
    day: d,
    hour: h,
    minute: m,
    date: resultDate,
  };
}

export function filterByCreatedByNull(data) {
  const result = {};

  for (const key in data) {
    if (Array.isArray(data[key])) {
      // filter only arrays of objects
      result[key] = data[key].filter(
        (item) =>
          !item.created_by ||
          item.created_by?.id === useProfileStore.getState().profile?.id,
      );
    } else {
      // keep other values as-is
      result[key] = data[key];
    }
  }

  return result;
}

export function sortByActiveId(rows, activeId) {
  return [...rows].sort((a, b) => {
    let id = parseInt(activeId);
    if (a.id === id) return -1; // a goes first
    if (b.id === id) return 1; // b goes first
    return 0; // keep original order otherwise
  });
}

export function getDateStatus(date) {
  // 1. Convert input to a Date object (handles strings or Date objects)
  const inputDate = new Date(date);

  // 2. Get the current date
  const today = new Date();

  // 3. Zero out the time components for both (set to Midnight 00:00:00)
  // This ensures we only compare the Day, Month, and Year.
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  // 4. Compare
  if (inputDate < today) {
    return "past";
  } else if (inputDate > today) {
    return "future";
  } else {
    return "present";
  }
}
function isEmoji(char) {
  // A comprehensive regex for detecting many types of Unicode emojis
  // It covers common ranges like:
  // - Emojis, Dingbats, and Transport/Map symbols
  // - Variation selectors (to distinguish text vs. emoji presentation)
  // - Regional indicator symbols (for flag emojis)
  // - Keycap base
  // - Zero Width Joiner (ZWJ) for complex emojis
  return /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1F1E6}-\u{1F1FF}]/gu.test(
    char,
  );
}

export function replaceEmojis(name) {
  let cleanedName = "";
  // The spread operator `...name` is important for handling multi-unit Unicode characters (emojis) correctly.
  for (const char of [...name]) {
    if (isEmoji(char)) {
      cleanedName += " "; // Replace emoji with a space
    } else {
      cleanedName += char; // Keep the character
    }
  }
  return cleanedName.replace(/\s+/g, " ").trim(); // Collapse multiple spaces and trim
}

export function getSupportedFiles() {
  return [
    "JPG",
    "JPEG",
    "PNG",
    "GIF",
    "PDF",
    "POT",
    "POTX",
    "PPT",
    "PPTX",
    "XLS",
    "XLSX",
    // "HTML",
    // "DOC",
    // "DOCX",
  ];
}

export function isUTF8(buffer) {
  const bytes = new Uint8Array(buffer);
  let i = 0;
  while (i < bytes.length) {
    if ((bytes[i] & 0x80) === 0) {
      // ASCII
      i++;
      continue;
    }
    if ((bytes[i] & 0xe0) === 0xc0) {
      // 2-byte sequence
      if (bytes.length < i + 2 || (bytes[i + 1] & 0xc0) !== 0x80) return false;
      i += 2;
      continue;
    }
    if ((bytes[i] & 0xf0) === 0xe0) {
      // 3-byte sequence
      if (
        bytes.length < i + 3 ||
        (bytes[i + 1] & 0xc0) !== 0x80 ||
        (bytes[i + 2] & 0xc0) !== 0x80
      )
        return false;
      i += 3;
      continue;
    }
    if ((bytes[i] & 0xf8) === 0xf0) {
      // 4-byte sequence
      if (
        bytes.length < i + 4 ||
        (bytes[i + 1] & 0xc0) !== 0x80 ||
        (bytes[i + 2] & 0xc0) !== 0x80 ||
        (bytes[i + 3] & 0xc0) !== 0x80
      )
        return false;
      i += 4;
      continue;
    }
    return false; // Invalid UTF-8 start byte
  }
  return true;
}

export function getActionType(data) {
  if (data?.actionType === "linkedin_vocal") {
    return "linkedin_message";
  }
  return data?.actionType;
}

export const needTimezone = [
  "time_start_sending",
  "time_start_sending_friday",
  "time_start_sending_monday",
  "time_start_sending_saturday",
  "time_start_sending_sunday",
  "time_start_sending_thursday",
  "time_start_sending_tuesday",
  "time_start_sending_wednesday",
  "time_stop_sending",
  "time_stop_sending_friday",
  "time_stop_sending_monday",
  "time_stop_sending_sunday",
  "time_stop_sending_thursday",
  "time_stop_sending_tuesday",
  "time_stop_sending_wednesday",
  "time_stop_sending_saturday",
];
export function getStepsFromNodes({ nodes, needDummy }) {
  let initialId = useFlowStore.getState().initialId;
  let timezoneOffset = getTimezoneOffset();

  return nodes.map((step, index) => {
    if (step?.data?.actionType !== "event") {
      let timeData = {};

      needTimezone.forEach((element) => {
        if (step?.data?.[element])
          timeData[element] = incrementHour(step?.data?.[element], 0);
      });

      return {
        step_id: {
          type: step?.data?.exist ? "existing" : "new",
          id: step.id === "0" ? 1 : parseInt(step.id),
        },
        step_type: "action",
        position_x: step.x,
        position_y: step.y,
        parent_ids:
          step?.data?.source === initialId || step.type === "start"
            ? []
            : Array.isArray(step?.data?.source)
              ? step?.data?.source.map((s) => ({
                  type: nodes.find((x) => x.id === s)?.data?.exist
                    ? "existing"
                    : "new",
                  id: parseInt(s),
                }))
              : [
                  {
                    type: nodes.find((x) => x.id === step.data.source)?.data
                      ?.exist
                      ? "existing"
                      : "new",
                    id:
                      step.data.source === "0" ? 1 : parseInt(step.data.source),
                  },
                ],
        action_type: step.type === "start" ? "dummy" : getActionType(step.data),
        model_id: step.data?.model_id,
        integration_id: step.data?.integration_id,
        pool_id: step.data?.pool_id,
        is_initial: needDummy
          ? step.type === "start"
          : step.data?.source === initialId,
        signature_id: step.data?.signature_id,
        limit_send_per_day:
          step.data?.limit_send_per_day ||
          defaultNodeConfigs?.limit_send_per_day,
        sending_weekends_allowed:
          step.data?.sending_weekends_allowed ||
          defaultNodeConfigs?.sending_weekends_allowed,
        time_start_sending:
          step.data?.time_start_sending ||
          defaultNodeConfigs?.time_start_sending,
        time_stop_sending: defaultNodeConfigs?.time_stop_sending,
        blacklist_ids:
          step.data?.blacklist_ids || defaultNodeConfigs?.blacklist_ids,
        email_linkedin_exclude:
          step.data?.email_linkedin_exclude ||
          defaultNodeConfigs?.email_linkedin_exclude,
        show_reply_as_conversation:
          step.data?.show_reply_as_conversation !== null &&
          step.data?.show_reply_as_conversation !== undefined
            ? step.data?.show_reply_as_conversation
            : defaultNodeConfigs?.show_reply_as_conversation,
        allow_massmailing:
          step.data?.show_reply_as_conversation ||
          defaultNodeConfigs?.allow_massmailing,
        use_smtp_fallback_server:
          step.data?.show_reply_as_conversation ||
          defaultNodeConfigs?.use_smtp_fallback_server,
        disable_auto_remove_responders:
          step.data?.disable_auto_remove_responders,
        ...timeData,
      };
    } else {
      return {
        step_id: {
          type: step?.data?.exist ? "existing" : "new",
          id: parseInt(step.id),
        },
        step_type: "event",
        event_type: step.data?.event_type,
        position_x: step.x,
        position_y: step.y,
        filter: step.data?.filter,
        which_links_clicked: step.data?.which_links_clicked,
        when_minutes:
          step.data?.when_minutes || defaultNodeConfigs?.when_minutes,
        name: step.data?.name,
        parent_ids: Array.isArray(step?.data?.source)
          ? step?.data?.source.map((s) => ({
              type: nodes.find((x) => x.id === s)?.data?.exist
                ? "existing"
                : "new",
              id: parseInt(s),
            }))
          : [
              {
                type: nodes.find((x) => x.id === step.data.source)?.data?.exist
                  ? "existing"
                  : "new",
                id: step.data.source === "0" ? 1 : parseInt(step.data.source),
              },
            ],
      };
    }
  });
}
//document.querySelector("#cw-bubble-holder button").click()
// window.$chatwoot.toggle("open")

export function snakeToCamel(snakeStr) {
  return snakeStr
    .toLowerCase() // Ensure the string is all lowercase
    .replace(
      /(_[a-z])/g,
      (match) => match[1].toUpperCase(), // Remove the underscore and capitalize the next letter
    );
}

export function camelToSnake(camelStr) {
  return camelStr
    .replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)
    .replace(/^_/, ''); // Sécurité : enlève le tiret du bas initial si c'était du PascalCase
}